import { type ReactNode } from "react";
import { fmtDate, type UserActivity } from "../activity";

function Section({
  title,
  icon,
  children,
}: {
  title: string;
  icon: string;
  children: ReactNode;
}) {
  return (
    <div>
      <h4 className="flex items-center gap-2 text-sm font-semibold text-foreground-800">
        <i className={`${icon} text-base text-foreground-500`} aria-hidden="true"></i>
        {title}
      </h4>
      <div className="mt-2">{children}</div>
    </div>
  );
}

function EmptyHint({ text }: { text: string }) {
  return <p className="text-sm text-foreground-500">{text}</p>;
}

export default function ActivityContent({ activity }: { activity: UserActivity }) {
  const { homework, notes, favorites, wrongQuestions } = activity;

  return (
    <div className="space-y-6">
      {/* Ödevler */}
      <Section title="Ödevler" icon="ri-task-line">
        {homework.length === 0 ? (
          <EmptyHint text="Henüz ödev yok." />
        ) : (
          <div className="space-y-2">
            {homework.map((h) => (
              <div
                key={h.id}
                className="flex items-start justify-between gap-3 rounded-md border border-background-200 bg-background-50 px-3 py-2.5"
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground-900 truncate">{h.title}</p>
                  <p className="text-xs text-foreground-500">
                    {h.subject ?? "—"}
                    {h.teacher_name ? ` · ${h.teacher_name}` : ""}
                    {h.due_date ? ` · Teslim: ${fmtDate(h.due_date)}` : ""}
                  </p>
                </div>
                {h.status && (
                  <span className="shrink-0 inline-flex items-center px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-900 text-xs font-semibold whitespace-nowrap">
                    {h.status}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* Notlar */}
      <Section title="Notlar" icon="ri-sticky-note-line">
        {notes.length === 0 ? (
          <EmptyHint text="Henüz not yok." />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {notes.map((n) => (
              <div
                key={n.id}
                className="rounded-md border border-background-200 bg-background-50 p-3"
              >
                <p className="text-sm font-medium text-foreground-900 truncate">{n.title}</p>
                {n.subject && <p className="text-xs text-foreground-500">{n.subject}</p>}
                {n.content && (
                  <p className="mt-1.5 text-xs text-foreground-600 line-clamp-3">{n.content}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* Favoriler */}
      <Section title="Favoriler" icon="ri-heart-3-line">
        {favorites.length === 0 ? (
          <EmptyHint text="Henüz favori yok." />
        ) : (
          <div className="flex flex-wrap gap-2">
            {favorites.map((f) => (
              <span
                key={f.id}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-background-100 text-xs text-foreground-700 whitespace-nowrap"
              >
                <i className="ri-heart-3-line text-accent-600" aria-hidden="true"></i>
                {f.title}
                {f.subject ? ` · ${f.subject}` : ""}
              </span>
            ))}
          </div>
        )}
      </Section>

      {/* Yanlış sorular */}
      <Section title="Yanlış Sorular" icon="ri-close-circle-line">
        {wrongQuestions.length === 0 ? (
          <EmptyHint text="Henüz yanlış soru kaydı yok." />
        ) : (
          <div className="space-y-2">
            {wrongQuestions.map((w) => (
              <div
                key={w.id}
                className="rounded-md border border-background-200 bg-background-50 px-3 py-2.5"
              >
                <p className="text-xs text-foreground-500">
                  {w.subject ?? "—"}
                  {w.topic ? ` · ${w.topic}` : ""}
                </p>
                <p className="mt-1 text-sm text-foreground-800 line-clamp-2">
                  {w.question ?? "—"}
                </p>
                <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-xs">
                  <span className="text-foreground-500">
                    Doğru: <span className="font-medium text-foreground-900">{w.correct_answer ?? "—"}</span>
                  </span>
                  <span className="text-foreground-500">
                    Verdiği: <span className="font-medium text-accent-800">{w.my_answer ?? "—"}</span>
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}